import { useEffect, useMemo } from 'react'
import { useEvent } from 'react-use-event-hook'
import { type SetURLSearchParams } from 'react-router'
import { clearCartRestoreParam, restoreCart, saveCart } from './userCartService'
import { FLOWS } from './userCart.consts'
import {
  buildTilePreviewSpecs,
  buildTilesRestorePayload,
  type TilesRestorePayload,
} from './tilesCartPayload'
import { analytics, EVENT_NAMES } from '../Analytics/Analytics'
import {
  getPhotoBankStore,
  getTilesStore,
  usePhotoBankStore,
} from 'stores/RootStoreProvider'
import { saveMaterialType } from '../../utils/materialTypeUtils'
import { setSelectedSize } from '../../hooks/tileStyling/utils'
import { type Tile } from '../../components/TilesProvider/TilesProvider.types'
import { useTiles } from '../../hooks/useTiles'
import useTileSize from '../../hooks/tileStyling/useTileSize'
import useTileMaterial from '../../hooks/tileStyling/useTileMaterial'
import debounce from 'lodash/debounce'
import partition from 'lodash/partition'
import { isAIGenPhoto, TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { toJS } from 'mobx'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import {
  type RawRemotePhoto,
  type RemotePhoto,
} from 'components/PhotoBank/PhotoBank.types'

const SAVE_DEBOUNCE_MS = 3000

export function useTilesUserCartSaver() {
  const photoBankStore = usePhotoBankStore()
  const { tiles }: { tiles: Tile[] } = useTiles()
  const { tileSize } = useTileSize()
  const { bulkMaterial } = useTileMaterial()

  const saveNow = useEvent(async () => {
    const tilesWithUid = tiles.filter(
      (t): t is Tile & { uid: string } => !!t.uid
    )
    const remoteTilePhotos = await Promise.all(
      tilesWithUid.map(({ uid }) =>
        readOutsideReaction(() =>
          photoBankStore
            .getRemotePhotoAsync(uid)
            .then((photo) => readOutsideReaction(() => toJS(photo)))
        )
      )
    )
    const remoteTilePhotosByUid = Object.fromEntries(
      remoteTilePhotos
        .filter((photo): photo is RemotePhoto => !!photo?.uid)
        .map((photo) => [photo.uid, photo])
    )

    const restorePayload = buildTilesRestorePayload({
      tiles,
      remotePhotoByUid: remoteTilePhotosByUid,
      tileSize,
      materialType: bulkMaterial,
    })
    const tilePreviewSpecs = buildTilePreviewSpecs({
      tiles,
      remotePhotoByUid: remoteTilePhotosByUid,
      tileSize,
      materialType: bulkMaterial,
    })

    // Check for AI tiles and build AI-specific params
    const aiTiles = tiles.filter(isAIGenPhoto)
    const aiEmailParams =
      aiTiles.length > 0
        ? {
            'Has AI Templates Tiles': true,
          }
        : {}

    saveCart({
      flow: FLOWS.TILE_DESIGNER,
      cartItems: [restorePayload],
      tilePreviewSpecs,
      emailTemplateParams: {
        'Has Tiles': tiles.length > 0,
        ...aiEmailParams,
      },
    })
  })

  const saveTilesToUserCart = useMemo(
    () => debounce(saveNow, SAVE_DEBOUNCE_MS),
    [saveNow]
  )

  // Flush (not cancel) — a pending save feeds cart restore and emails
  useEffect(
    () => () => {
      saveTilesToUserCart.flush()
    },
    [saveTilesToUserCart]
  )

  return {
    saveTilesToUserCart,
  }
}

export function restoreTilesFromPayload(payload: TilesRestorePayload): number {
  const { tiles, tileSize, materialType } = payload
  const [restoredAiTiles, restoredPhotoTiles] = partition(
    tiles,
    (tile) => tile.tileContent === TILE_CONTENT.AI_GEN_PHOTO
  )
  const photoBankStore = getPhotoBankStore()
  photoBankStore.appendPhotos({
    photos: restoredPhotoTiles as unknown as RawRemotePhoto[],
    uploadSrc: UPLOAD_SRC.TILE_DESIGNER,
  })
  photoBankStore.appendPhotos({
    photos: restoredAiTiles as unknown as RawRemotePhoto[],
    uploadSrc: UPLOAD_SRC.AI_RESULT,
  })
  getTilesStore().restoreRawTiles(tiles)
  setSelectedSize(tileSize)
  saveMaterialType(materialType)
  return tiles.length
}

export const updateTilesFromUserCart = async (
  setSearchParams: SetURLSearchParams
) => {
  const { cartId, userCart } = await restoreCart(FLOWS.TILE_DESIGNER)
  if (!userCart) {
    return
  }
  clearCartRestoreParam(FLOWS.TILE_DESIGNER, setSearchParams)

  const payload = userCart.cartItems[0]
  if (!payload) {
    return
  }
  const restoredTilesCount = restoreTilesFromPayload(payload)

  analytics.track(EVENT_NAMES.CART_RESTORED, {
    Flow: FLOWS.TILE_DESIGNER,
    'Restored Tiles Count': restoredTilesCount,
    'Restored Tile Size': payload.tileSize,
    'Restored Material Type': payload.materialType,
    'Restored Cart ID': cartId,
  })
}
